"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useEffect, useRef } from "react";
export default function ContactForm() {
  return <section id="contact-form" className="relative min-h-screen pt-32 pb-20 bg-background overflow-hidden">
      <FormProvider formId="contact_form_contact" sectionName="contact-form" className="container mx-auto px-4" defaultValues={{
      name: "",
      phone: "",
      email: "",
      service_needed: "",
      event_date: "",
      location: "",
      budget_range: "",
      message: ""
    }} validationRules={{
      name: {
        required: true,
        minLength: 2,
        maxLength: 100
      },
      phone: {
        required: true,
        pattern: "phone"
      },
      email: {
        pattern: "email"
      },
      service_needed: {
        required: true
      },
      message: {
        required: true,
        minLength: 20,
        maxLength: 1000
      }
    }} submitText="Send Your Inquiry" successMessage="Thank you! We'll review your request and get back to you within 24 hours." errorMessage="Something went wrong. Please check your details and try again.">
        <ContactFormContent />
      </FormProvider>
    </section>;
}
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll({
    threshold: 0.2,
    once: true
  });
  const parallaxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (parallaxRef.current && window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
        const scrolled = window.scrollY;
        const rect = parallaxRef.current.getBoundingClientRect();
        const inView = rect.top < window.innerHeight && rect.bottom > 0;
        if (inView) {
          const offsetY = scrolled * 0.3;
          parallaxRef.current.style.transform = `translateY(${offsetY}px)`;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  if (isSubmitted && !submitError) {
    return <div className="max-w-2xl mx-auto text-center" style={{
      animation: "fade-in-up 200ms cubic-bezier(0.4, 0, 0.2, 1) both"
    }}>
        <div className="p-8 rounded-lg bg-accent-light border-2 border-accent shadow-medium">
          <h2 className="text-3xl font-display font-bold text-primary mb-4">Message Sent!</h2>
          <p className="text-lg text-foreground mb-6">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="hover:bg-accent hover:text-foreground hover:border-accent transition-all duration-[200ms]">
            Send Another Message
          </Button>
        </div>
      </div>;
  }
  return <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
      <div ref={leftRef} className="space-y-6" style={{
      opacity: leftVisible ? 1 : 0,
      transform: leftVisible ? "translateY(0)" : "translateY(20px)",
      transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1), transform 200ms cubic-bezier(0.4, 0, 0.2, 1)"
    }}>
        <div className="mb-8">
          <h1 className="text-4xl lg:text-5xl font-display font-bold text-primary mb-3">
            Contact Form
          </h1>
          <p className="text-lg text-muted-foreground">
            Share your project details and we'll craft a custom proposal for you.
          </p>
        </div>

        <Form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <FormField control={control} name="name" rules={{
            required: "Full name is required",
            minLength: {
              value: 2,
              message: "Name must be at least 2 characters"
            },
            maxLength: {
              value: 100,
              message: "Name must be less than 100 characters"
            }
          }} render={({
            field
          }) => <FormItem data-index={0}>
                  <FormLabel>Full Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Mohammed" {...field} className="focus:border-accent focus:ring-accent/50 transition-all duration-[200ms]" />
                  </FormControl>
                  <FormDescription>Enter your first and last name.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="phone" rules={{
            required: "Phone number is required",
            pattern: {
              value: /^[\d\s\-\+\(\)]+$/,
              message: "Please enter a valid phone number"
            }
          }} render={({
            field
          }) => <FormItem data-index={1}>
                  <FormLabel>Phone / WhatsApp</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="+966 (5xx) xxx-xxxx" {...field} className="focus:border-accent focus:ring-accent/50 transition-all duration-[200ms]" />
                  </FormControl>
                  <FormDescription>We may contact you via WhatsApp or call.</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="email" rules={{
            pattern: {
              value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
              message: "Please enter a valid email address"
            }
          }} render={({
            field
          }) => <FormItem data-index={2}>
                  <FormLabel>Email Address (Optional)</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="Mohammed@example.com" {...field} className="focus:border-accent focus:ring-accent/50 transition-all duration-[200ms]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="service_needed" rules={{
            required: "Please select a service"
          }} render={({
            field
          }) => <FormItem data-index={3}>
                  <FormLabel>Service Needed</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-accent focus-visible:ring-accent/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                      <option value="">Select a service</option>
                      <option value="Video Production">Video Production</option>
                      <option value="Photography">Photography</option>
                      <option value="Event Coverage">Event Coverage</option>
                      <option value="Social Media Content">Social Media Content</option>
                      <option value="Post-Production">Post-Production</option>
                      <option value="Other">Other</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="event_date" render={({
            field
          }) => <FormItem data-index={4}>
                  <FormLabel>Preferred Date</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} className="focus:border-accent focus:ring-accent/50 transition-all duration-[200ms]" />
                  </FormControl>
                  <FormDescription>Select the date of your event or shoot (if applicable).</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="location" render={({
            field
          }) => <FormItem data-index={5}>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <Input placeholder="Saudi Arabian cities (e.g., Riyadh, Jeddah)" {...field} className="focus:border-accent focus:ring-accent/50 transition-all duration-[200ms]" />
                  </FormControl>
                  <FormDescription>Where will the work take place?</FormDescription>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="budget_range" render={({
            field
          }) => <FormItem data-index={6}>
                  <FormLabel>Budget Range</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-all outline-none focus-visible:border-accent focus-visible:ring-accent/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm">
                      <option value="">Select a range</option>
                      <option value="Under $5,000">Under SAR 5,000</option>
                      <option value="$5,000 – $10,000">SAR 5,000 – SAR 10,000</option>
                      <option value="$10,000 – $20,000">SAR 10,000 – SAR 20,000</option>
                      <option value="$20,000+">SAR 20,000+</option>
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="message" rules={{
            required: "Please provide project details",
            minLength: {
              value: 20,
              message: "Message must be at least 20 characters"
            },
            maxLength: {
              value: 1000,
              message: "Message must be less than 1000 characters"
            }
          }} render={({
            field
          }) => <FormItem data-index={7}>
                  <FormLabel>Message / Details</FormLabel>
                  <FormControl>
                    <textarea {...field} rows={5} placeholder="Tell us about your project, goals, and any special requirements." className="flex min-h-[120px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-xs transition-all outline-none focus-visible:border-accent focus-visible:ring-accent/50 focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 md:text-sm resize-y" />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 rounded-md bg-red-50 border border-red-200">
                <p className="text-red-800 text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-accent text-foreground hover:bg-accent-hover hover:shadow-medium hover:-translate-y-0.5 active:bg-accent-active active:translate-y-0 focus-visible:ring-accent/40 transition-all duration-[200ms] ease-[var(--ease-spring)]">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-5" />
                  Sending...
                </> : 'Send Your Inquiry'}
            </Button>
          </div>
        </Form>
      </div>

      <div ref={rightRef} className="relative h-[600px] lg:h-[800px] rounded-lg overflow-hidden shadow-medium" style={{
      opacity: rightVisible ? 1 : 0,
      transform: rightVisible ? "translateY(0)" : "translateY(20px)",
      transition: "opacity 200ms cubic-bezier(0.4, 0, 0.2, 1) 100ms, transform 200ms cubic-bezier(0.4, 0, 0.2, 1) 100ms"
    }}>
        <div ref={parallaxRef} className="absolute inset-0 bg-cover bg-center bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1529178/img/contact_form_abstract_visual.png)]" style={{
        height: "120%",
        top: "-10%"
      }} />
      </div>
    </div>;
}